// pages/DocViewer.jsx — renders a single documentation page (admin only).
// Fetches markdown from /wiki/:slug and renders it with react-markdown.
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import Header from "../components/Header";
import { authFetch } from "../api";
import "./Docs.css";

export default function DocViewer() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setDoc(null);
    setError(null);
    authFetch(`/wiki/${slug}`)
      .then((data) => {
        if (data && typeof data.markdown === "string") {
          setDoc(data);
        } else {
          setError(data?.detail || "Could not load this page.");
        }
      })
      .catch(() => setError("Could not load this page."));
  }, [slug]);

  // Custom renderers: keep in-app navigation for relative links (doc slugs),
  // open external links in a new tab, and let wide tables scroll.
  const components = {
    a({ href = "", children, ...props }) {
      const isExternal = /^(https?:)?\/\//i.test(href) || href.startsWith("mailto:");
      if (isExternal) {
        return (
          <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
            {children}
          </a>
        );
      }
      // Relative href = another doc slug (strip any leading ./ or /).
      const target = href.replace(/^\.?\/*/, "");
      return (
        <a
          href={`/docs/${target}`}
          onClick={(e) => {
            e.preventDefault();
            navigate(`/docs/${target}`);
          }}
          {...props}
        >
          {children}
        </a>
      );
    },
    table({ children }) {
      return (
        <div className="table-scroll">
          <table>{children}</table>
        </div>
      );
    },
  };

  return (
    <>
      <Header showLogout />
      <div className="docs-shell">
        <div className="docs-wrap">
          <button className="docs-back" onClick={() => navigate("/docs")}>
            &larr; All docs
          </button>

          {error && <div className="docs-state">{error}</div>}
          {!doc && !error && <div className="docs-state">Loading…</div>}

          {doc && (
            <article className="doc-article">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
                {doc.markdown}
              </ReactMarkdown>
            </article>
          )}
        </div>
      </div>
    </>
  );
}
